// src/components/Admin/Menu_Management/CategoryView.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Edit2,
  Trash2,
  FolderOpen,
  CheckCircle,
  XCircle,
  RefreshCw,
  Plus,
} from "lucide-react";
import toast from "react-hot-toast";
import CategoryAddEditForm from "./CategoryAddEditForm";
import categoryService from "../../../services/categoryService";
import useTax from "../../../hooks/useTax";
import { ConfirmDialog } from "./UIComponents";
import Pagination from "../../shared/Pagination";
import { useAuth } from '../../../context/AuthContext';
import { P } from '@/config/permissionCodes';
import { extractApiMessage, isDuplicateError } from '@/utils/apiError';


export default function CategoryView({ branchId }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  // Field-level error handed back to the form (e.g. duplicate name from the API).
  const [formServerError, setFormServerError] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [filterText, setFilterText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortConfig, setSortConfig] = useState({
    key: "displayOrder",
    direction: "asc",
  });
  const { hasPermission } = useAuth();
const canAddCat    = hasPermission(P.BACK_OFFICE.MENU.CATEGORIES.ADD);
const canEditCat   = hasPermission(P.BACK_OFFICE.MENU.CATEGORIES.EDIT);
const canDeleteCat = hasPermission(P.BACK_OFFICE.MENU.CATEGORIES.DELETE);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    type: "",
    data: null,
  });

  const showToast = (message, type = "success") => {
    if (type === "success") toast.success(message);
    else if (type === "error") toast.error(message);
    else toast(message);
  };

  // Get taxes for display
  const { taxes } = useTax();

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await categoryService.getAll();
      const data = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
        ? res.data
        : [];
      setCategories(data);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
      showToast("Failed to fetch categories", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Get tax name by ID - convert to number for comparison
  const getTaxName = (taxId) => {
    if (!taxId) return null;
    const numericTaxId = Number(taxId);
    const tax = taxes.find((t) => Number(t.id) === numericTaxId);
    return tax ? `${tax.name} (${tax.rate}%)` : null;
  };

  // Handle save (create/update)
  const handleSave = async (data) => {
    setFormServerError(null);
    try {
      if (editItem) {
        await categoryService.update(editItem.id, data);
        showToast("Category updated successfully", "success");
      } else {
        await categoryService.create(data);
        showToast("Category created successfully", "success");
      }
      setShowForm(false);
      setEditItem(null);
      fetchCategories();
    } catch (error) {
      console.error("Failed to save category:", error);
      const message = extractApiMessage(
        error,
        "Failed to save category. Please try again."
      );

      // Duplicate name is a field problem: keep the form open and point at the field
      // instead of a toast the user can do nothing about.
      if (isDuplicateError(error, message)) {
        setFormServerError({
          field: "name",
          message: `A category named "${data.name}" already exists. Please choose a different name.`,
        });
        showToast("That category name is already taken", "error");
        return;
      }

      showToast(message, "error");
    }
  };

  // Categories the form should treat as name-taken. Scoped to the branch being
  // viewed because the server's duplicate check runs under the tenantFilter
  // (branch_id = :branchId). Deliberately not reusing filteredCategories — that
  // also applies search/status filters, which would hide a genuine clash.
  const categoriesInScope = useMemo(
    () => (branchId ? categories.filter((c) => c.branchId === branchId) : categories),
    [categories, branchId]
  );

  // Filter categories
  const filteredCategories = categories.filter((cat) => {
    // Branch filter (for multi-branch owners)
    if (branchId && cat.branchId !== branchId) return false;

    // Status filter
    if (statusFilter === "active" && !cat.isActive) return false;
    if (statusFilter === "inactive" && cat.isActive) return false;

    // Text search
    const text = filterText.toLowerCase();
    const taxName = getTaxName(cat.taxId) || "";
    return (
      cat.name?.toLowerCase().includes(text) ||
      cat.description?.toLowerCase().includes(text) ||
      taxName.toLowerCase().includes(text)
    );
  });

  // Sort categories
  const sortedCategories = React.useMemo(() => {
    let sortable = [...filteredCategories];

    if (sortConfig.key !== null) {
      sortable.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (aValue === null || aValue === undefined) aValue = "";
        if (bValue === null || bValue === undefined) bValue = "";

        const aNum = Number(aValue);
        const bNum = Number(bValue);
        const bothNumeric =
          !isNaN(aNum) && !isNaN(bNum) && aValue !== "" && bValue !== "";

        if (bothNumeric) {
          if (aNum < bNum) return sortConfig.direction === "asc" ? -1 : 1;
          if (aNum > bNum) return sortConfig.direction === "asc" ? 1 : -1;
          return 0;
        }

        aValue = aValue.toString().toLowerCase();
        bValue = bValue.toString().toLowerCase();

        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return sortable;
  }, [filteredCategories, sortConfig]);

  // Pagination calculations
  const totalItems = sortedCategories.length;
  const totalPages = totalItems === 0 ? 1 : Math.ceil(totalItems / pageSize);

  // Reset page when filters change
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  // Reset to page 1 when filter/search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterText, statusFilter]);

  // Get paginated categories
  const paginatedCategories = useMemo(() => {
    if (loading) return [];
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedCategories.slice(startIndex, endIndex);
  }, [sortedCategories, currentPage, pageSize, loading]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown size={14} className="text-gray-400" />;
    }
    return sortConfig.direction === "asc" ? (
      <ArrowUp size={14} className="text-teal-600" />
    ) : (
      <ArrowDown size={14} className="text-teal-600" />
    );
  };

  // Delete handlers
  const handleDeleteCategory = (id) => {
    setConfirmDialog({
      isOpen: true,
      type: "delete",
      data: id,
    });
  };

  const confirmDelete = async () => {
    try {
      await categoryService.delete(confirmDialog.data);
      showToast("Category deleted successfully", "success");
      fetchCategories();
    } catch (error) {
      console.error("Failed to delete category:", error);
      showToast("Failed to delete category", "error");
    } finally {
      setConfirmDialog({ isOpen: false, type: "", data: null });
    }
  };

  const confirmConfig =
    confirmDialog.type === "delete"
      ? {
          title: "Delete Category",
          message:
            "Are you sure you want to delete this category? All menu items under this category will also be permanently deleted.",
          confirmText: "Delete",
          type: "danger",
          onConfirm: confirmDelete,
        }
      : null;

  return (
    <div className="min-h-full bg-gray-50">
      <div className="p-6 space-y-5">
        {/* Action Bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={fetchCategories}
              className="p-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors border border-gray-200"
              title="Refresh"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
          {canAddCat && (
  <button
    onClick={() => { setEditItem(null); setShowForm(true); }}
    className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
  >
    <Plus size={16} />
    Add Category
  </button>
)}

        </div>
        {/* Search & Filters */}
        <div className="flex items-center gap-4">
          <div className="flex-1 max-w-md relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Search by name, description, or tax..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th
                    onClick={() => handleSort("displayOrder")}
                    className="text-left px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors w-16"
                  >
                    <div className="flex items-center gap-2">
                      #
                      {getSortIcon("displayOrder")}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("name")}
                    className="text-left px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Category
                      {getSortIcon("name")}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("taxId")}
                    className="text-left px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Tax
                      {getSortIcon("taxId")}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("isActive")}
                    className="text-left px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      Status
                      {getSortIcon("isActive")}
                    </div>
                  </th>
                 {(canEditCat || canDeleteCat) && (
  <th className="text-center px-4 py-3 text-xs font-bold text-gray-700 uppercase tracking-wide">
    Actions
  </th>
)}
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td className="px-4 py-12 text-center text-gray-500" colSpan={5}>
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-gray-200 border-t-teal-600 rounded-full animate-spin"></div>
                        <p className="text-sm font-medium">Loading...</p>
                      </div>
                    </td>
                  </tr>
                ) : paginatedCategories.length === 0 ? (
                  <tr>
                    <td className="px-4 py-12 text-center text-gray-500" colSpan={5}>
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                          <FolderOpen className="w-8 h-8 text-gray-400" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            No categories found
                          </p>
                          <p className="text-sm text-gray-500 mt-1">
                            {filterText
                              ? "Try adjusting your search terms"
                              : "Get started by adding your first category"}
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedCategories.map((category, index) => (
                    <tr key={category.id} className="hover:bg-gray-50">
                      {/* Order */}
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center justify-center w-7 h-7 bg-teal-50 text-teal-700 rounded-lg text-xs font-bold">
                          {category.displayOrder || index + 1}
                        </span>
                      </td>

                      {/* Category Name & Description */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <FolderOpen className="w-4 h-4 text-teal-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {category.name}
                            </p>
                            {category.description && (
                              <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                                {category.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Tax */}
                      <td className="px-4 py-3">
                        {category.taxId ? (
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-teal-50 text-teal-700">
                              {getTaxName(category.taxId)}
                            </span>
                            {category.inclusiveTaxRate && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded font-medium">
                                Inclusive
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">
                            No tax assigned
                          </span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        {category.isActive ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700">
                            <CheckCircle className="w-3.5 h-3.5" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            <XCircle className="w-3.5 h-3.5" />
                            Inactive
                          </span>
                        )}
                      </td>

                      {/* Actions */}
                      {(canEditCat || canDeleteCat) && (
  <td className="px-4 py-3 text-center">
    <div className="inline-flex items-center gap-1">
      {canEditCat && (
        <button onClick={() => { setEditItem(category); setShowForm(true); }} className="p-1.5 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="Edit">
          <Edit2 size={15} />
        </button>
      )}
      {canDeleteCat && (
        <button onClick={() => handleDeleteCategory(category.id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
          <Trash2 size={15} />
        </button>
      )}
    </div>
  </td>
)}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {!loading && sortedCategories.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg px-4">
            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              pageSizeOptions={[10, 20, 50]}
            />
          </div>
        )}
      </div>

      {/* Add/Edit Form Modal */}
{showForm && (canAddCat || canEditCat) && (
        <CategoryAddEditForm
          item={editItem}
          onClose={() => {
            setShowForm(false);
            setEditItem(null);
            setFormServerError(null);
          }}
          onSave={handleSave}
          serverError={formServerError}
          onClearServerError={() => setFormServerError(null)}
          // Branch-scoped, matching the server's own duplicate check (which runs
          // under the tenantFilter) — the same name IS allowed in another branch.
          existingCategories={categoriesInScope}
        />
      )}

      {/* Confirm dialog */}
      {confirmConfig && (
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          onClose={() => setConfirmDialog({ isOpen: false, type: "", data: null })}
          {...confirmConfig}
        />
      )}
    </div>
  );
}
